import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingBag,
  Menu,
  Settings,
  Users,
  Bell,
  TrendingUp,
  Store,
  LogOut,
  Globe,
  MessageCircle,
  DollarSign,
  CreditCard,
  Wallet,
  TrendingUp as Growth,
  Sparkles,
  GroupIcon
  // Bot,
  // Activity,
  // Shield,
  // BarChart3,
  // Trophy,
  // Users2,
  // Brain,
  // Target,
  // Gamepad2,
} from 'lucide-react';
import { useSocket } from '@/contexts/SocketContext';
import { useAuthStore } from '@/stores/auth.store';
import { useLogout } from '@/hooks/auth/useAuth';
import { useGetVendor } from '@/hooks/vendor/useVendor';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, } = useAuthStore();
  const { data } = useGetVendor()
  const tenant = data?.tenant
  const { isConnected } = useSocket();

  const { mutateAsync: logout } = useLogout()

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      description: 'Overview and metrics',
    },
    {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingBag,
      description: 'Manage incoming orders',
    },
    {
      name: 'Menu',
      href: '/menu',
      icon: Menu,
      description: 'Manage your menu items',
    },
    {
      name: 'Category',
      href: '/categories',
      icon: GroupIcon,
      description: 'Manage your categories',
    },
    {
      name: 'Branches',
      href: '/branches',
      icon: Store,
      description: 'Manage your locations',
    },
    {
      name: 'Advanced Analytics',
      href: '/advanced-analytics',
      icon: TrendingUp,
      description: 'Financial & revenue insights',
    },
    {
      name: 'Engagement',
      href: '/engagement',
      icon: Sparkles,
      description: 'Track your profile & activity',
    },
    {
      name: 'AI Analytics Dashboard',
      href: '/ai-analytics-dashboard',
      icon: Growth,
      description: 'Advanced AI insights & predictions',
    },

    {
      name: 'Inventory',
      href: '/inventory-management',
      icon: Store,
      description: 'Smart inventory management',
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: Users,
      description: 'Customer management',
    },
    {
      name: 'Website',
      href: '/website',
      icon: Globe,
      description: 'Manage your restaurant website',
    },



    {
      name: 'WhatsApp Management',
      href: '/whatsapp',
      icon: MessageCircle,
      description: 'Manage customer conversations',
    },
    {
      name: 'Payment Management',
      href: '/payment-management',
      icon: CreditCard,
      description: 'Advanced payment processing',
    },
    {
      name: 'Wallet Management',
      href: '/wallet-management',
      icon: Wallet,
      description: 'Advanced wallet & balance management',
    },
    // {
    //   name: 'Commission Management',
    //   href: '/commission-management',
    //   icon: PiggyBank,
    //   description: 'Track commissions & payouts',
    // },
    {
      name: 'Payout Monitoring',
      href: '/payout-monitoring',
      icon: DollarSign,
      description: 'Monitor payouts and transactions',
    },
    //     {
    //   name: 'Rider Incentives',
    //   href: '/rider-incentives',
    //   icon: Zap,
    //   description: 'Manage rider rewards and bonuses',
    // },
    // {
    //   name: 'AI Insights',
    //   href: '/ai-analytics',
    //   icon: Bot,
    //   description: 'AI-powered insights',
    // },
    // {
    //   name: 'Personalization',
    //   href: '/personalization',
    //   icon: Brain,
    //   description: 'AI personalization and recommendations',
    // },
    // {
    //   name: 'Customer Personalization',
    //   href: '/customer-personalization',
    //   icon: Target,
    //   description: 'Advanced customer insights & targeting',
    // },
    // {
    //   name: 'Gamification',
    //   href: '/gamification',
    //   icon: Trophy,
    //   description: 'Challenges and competitions',
    // },
    // {
    //   name: 'Vendor Gamification',
    //   href: '/vendor-gamification',
    //   icon: Gamepad2,
    //   description: 'Vendor-specific achievements & rewards',
    // },
    // {
    //   name: 'Team Gamification',
    //   href: '/team-gamification',
    //   icon: Users2,
    //   description: 'Team competitions and collaboration',
    // },

    // {
    //   name: 'Market Intel',
    //   href: '/competitive-intelligence',
    //   icon: BarChart3,
    //   description: 'Competitive analysis',
    // },
    //     {
    //   name: 'WhatsApp Takeover',
    //   href: '/whatsapp-takeover',
    //   icon: Shield,
    //   description: 'Manual conversation control',
    // },
    //   {
    //   name: 'AI Website Builder',
    //   href: '/ai-website-generator',
    //   icon: Bot,
    //   description: 'AI-powered website creation',
    // },
    // {
    //   name: 'System Monitoring',
    //   href: '/monitoring',
    //   icon: Activity,
    //   description: 'Monitor system performance',
    // },
  ];


  const secondaryItems = [
    {
      name: 'Notifications',
      href: '/notifications',
      icon: Bell,
      description: 'View all notifications',
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      description: 'Restaurant settings',
    },
  ];


  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:inset-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <img
                src="/images/Umenda.svg"
                alt="Umenda Logo"
                className="h-8 w-auto"
              />
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Vendor Portal</h1>
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'
                      }`}
                  />
                  <span className="text-xs text-gray-500">
                    {isConnected ? 'Connected' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Restaurant Info */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <Store className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {tenant?.name || '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {tenant?.settings?.acceptingOrders ? (
                    <span className="text-green-600">● Open</span>
                  ) : (
                    <span className="text-red-600">● Closed</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <div className="space-y-1">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={`
                      sidebar-item group
                      ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}
                    `}
                    onClick={() => onClose()}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{item.name}</div>
                      <div className="text-xs opacity-75">{item.description}</div>
                    </div>
                  </NavLink>
                );
              })}
            </div>

            <div className="pt-6 mt-6 border-t border-gray-200">
              <div className="space-y-1">
                {secondaryItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={`
                        sidebar-item group
                        ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}
                      `}
                      onClick={() => onClose()}
                    >
                      <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{item.name}</div>
                        <div className="text-xs opacity-75">{item.description}</div>
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.role || 'vendor'}
                </p>
              </div>
            </div>

            <button
              onClick={() => logout()}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;